import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { refrescarBusqueda } from '@lib/refrescarBusqueda';
import { Search, X, Mail, PhoneCall, Copy, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { TableSkeleton, LinesSkeleton, Spinner } from '@components/medicus-ui/Loaders';
import { EstadoLegajoBadge } from '@components/EstadoLegajoBadge';
import { EstadoPersonaBadge } from '@components/EstadoPersonaBadge';
import {
  AvisoAtribucionModal,
  AvisoAtribucionTexto,
  type AgenteAtribuido,
} from '@components/AvisoAtribucionOferta';
import { useMediaQuery } from '@lib/hooks/useMediaQuery';
import {
  buscarPersonas,
  getLegajoDetalle,
  getGrupoBorradorDetalle,
  reportarInteresadoSkytel,
  type BusquedaMatchItem,
  type LegajoDetalle,
  type LegajoDetalleAsociado,
  type LegajoDetalleObservacion,
  type Ms2AltaResumen,
} from '@lib/services/busqueda.service';
import {
  historialSyncBinary,
  sincronizarLegajoBinary,
  retitularizarLegajo,
  syncEsReciente,
  type Divergencia,
  type RespuestaSync,
  type SyncBinaryEvento,
} from '@lib/services/sync-binary.service';
import { resumirSync, textoVinculadas } from '@lib/sync-binary-resumen';
import { copiarLinkOferta, enviarOfertaTmk } from '@lib/services/oferta.service';
import { useAuthStore } from '@lib/store/auth.store';
import { UserRole, esTelemarketer, isMedicus } from '@lib/types';

function fmtMoney(n: number): string {
  return n.toLocaleString('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  });
}

const CAMPO_LABEL: Record<string, string> = {
  documento: 'Documento',
  cuil: 'CUIL',
  fechaNacimiento: 'Fecha de nacimiento',
  nombres: 'Nombres',
  apellido: 'Apellido',
  apellSolt: 'Apellido',
  sexo: 'Sexo',
  estadoCivil: 'Estado civil',
  nacionalidad: 'Nacionalidad',
  tipoDoc: 'Tipo de documento',
  parentesco: 'Parentesco',
};

const PARENTESCO_LABEL: Record<string, string> = {
  '0': 'Titular',
  '5': 'Cónyuge',
  '7': 'Hijo/a',
  '13': 'Adherente',
};

// ── Badges de datos reales de Binary (plan + pago) ──
const FECHA_AR = new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const FECHA_HORA_AR = new Intl.DateTimeFormat('es-AR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * dd/mm/aaaa desde un ISO, un `YYYY-MM-DD` o un Date. Un `YYYY-MM-DD` pelado se
 * arma a mano: `new Date` lo toma como UTC y en Argentina cae al día anterior.
 */
function fmtFecha(v: string | Date | null | undefined): string {
  if (!v) return '';
  const s = typeof v === 'string' ? v : v.toISOString();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : FECHA_AR.format(d);
}

/** dd/mm/aaaa hh:mm desde un ISO o un Date. */
function fmtFechaHora(v: string | Date | null | undefined): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : FECHA_HORA_AR.format(d).replace(',', '');
}

function MiniBadge(props: { text: string; bg: string; fg: string; title?: string }) {
  return (
    <span
      title={props.title}
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 10,
        background: props.bg,
        color: props.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {props.text}
    </span>
  );
}

/** Plan real en Binary (del sync de altas). null = legajo no sincronizado. */
function PlanBinaryBadge({ ms2Alta }: { ms2Alta?: Ms2AltaResumen | null }) {
  if (!ms2Alta) return <span style={{ color: 'var(--color-element-mid)' }}>—</span>;
  if (!ms2Alta.afiliadoActivo) {
    return <MiniBadge text="No activo" bg="#e2e8f0" fg="#475569" title="No figura como afiliado activo en Binary" />;
  }
  if (ms2Alta.plan === 'MS2') return <MiniBadge text="MS2" bg="#dbeafe" fg="#1d4ed8" title={ms2Alta.planCodigo ?? ''} />;
  if (ms2Alta.plan === 'MS1') return <MiniBadge text="MS1" bg="#f1f5f9" fg="#334155" title={ms2Alta.planCodigo ?? ''} />;
  return <MiniBadge text={ms2Alta.planCodigo || 'otro'} bg="#fef3c7" fg="#92400e" title="Plan fuera de MS1/MS2" />;
}

const PAGO_BADGE: Record<string, { bg: string; fg: string }> = {
  aprobado: { bg: '#dcfce7', fg: '#15803d' },
  pendiente: { bg: '#fef3c7', fg: '#92400e' },
  rechazado: { bg: '#fee2e2', fg: '#b91c1c' },
  cancelado: { bg: '#e2e8f0', fg: '#475569' },
};
function PagoBadge({ estado }: { estado?: string | null }) {
  if (!estado) return <span style={{ color: 'var(--color-element-mid)' }}>—</span>;
  const c = PAGO_BADGE[estado] ?? { bg: '#e2e8f0', fg: '#475569' };
  return <MiniBadge text={estado} bg={c.bg} fg={c.fg} />;
}

export function BusquedaPage() {
  // El estado vive en la URL (?q, ?legajo, ?asociado) para poder compartir el
  // link, usar atrás/adelante del browser y —sobre todo— abrir el detalle de
  // una persona con un deep-link (?legajo=ID) sin tener que buscarla.
  const [searchParams, setSearchParams] = useSearchParams();
  const submitted = (searchParams.get('q') ?? '').trim();
  const legajoParam = searchParams.get('legajo');
  // Detalle de un grupo en padrón borrador (sin legajo): se abre por (padrón, número).
  const padronParam = searchParams.get('padron');
  const numeroParam = searchParams.get('numero');
  const asociadoParam = searchParams.get('asociado') ?? undefined;
  const [query, setQuery] = useState(submitted);

  // Si el `q` de la URL cambia desde afuera (deep link, botón atrás), reflejarlo
  // en el input.
  useEffect(() => {
    setQuery(submitted);
  }, [submitted]);

  const q = useQuery({
    queryKey: ['busqueda', submitted],
    queryFn: () => buscarPersonas(submitted),
    enabled: submitted.length >= 2,
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const v = query.trim();
    const next = new URLSearchParams(searchParams);
    if (v) next.set('q', v);
    else next.delete('q');
    next.delete('legajo'); // una búsqueda nueva cierra el detalle abierto
    next.delete('padron');
    next.delete('numero');
    next.delete('asociado');
    setSearchParams(next);
  }

  function limpiar() {
    setQuery('');
    setSearchParams(new URLSearchParams());
  }

  function abrir(m: BusquedaMatchItem) {
    const next = new URLSearchParams(searchParams);
    if (m.legajoId) {
      // Caso normal: hay legajo → detalle por legajoId.
      next.set('legajo', m.legajoId);
      next.delete('padron');
      next.delete('numero');
    } else {
      // Padrón borrador (sin legajo): detalle por (padrón, número de legajo).
      next.set('padron', m.padronId);
      next.set('numero', m.legajoNumero);
      next.delete('legajo');
    }
    if (m.matchTipo === 'familiar') next.set('asociado', m.asociadoId);
    else next.delete('asociado');
    setSearchParams(next);
  }

  function cerrar() {
    const next = new URLSearchParams(searchParams);
    next.delete('legajo');
    next.delete('padron');
    next.delete('numero');
    next.delete('asociado');
    setSearchParams(next);
  }

  /** Abre el detalle de un legajo por su id (ej. saltar a un legajo hermano). */
  function abrirLegajoId(legajoId: string) {
    const next = new URLSearchParams(searchParams);
    next.set('legajo', legajoId);
    next.delete('padron');
    next.delete('numero');
    next.delete('asociado');
    setSearchParams(next);
  }

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Búsqueda</h1>
        <p className="text-element-mid">
          Buscá personas por <strong>DNI</strong>, <strong>CUIL</strong>,{' '}
          <strong>número de legajo</strong> o <strong>nombre/apellido</strong> a través de todos
          los padrones a los que tenés acceso.
        </p>
      </header>

      <form onSubmit={handleSubmit} style={{ marginTop: 20, display: 'flex', gap: 8 }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 12px',
            border: '1px solid var(--color-outline)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-background)',
          }}
        >
          <Search size={16} strokeWidth={2} style={{ opacity: 0.6 }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ej: 30123456, 27301234567, GN001234, Juan Pérez"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              padding: '10px 0',
              background: 'transparent',
              fontSize: 14,
            }}
            autoFocus
          />
          {query && (
            <button
              type="button"
              onClick={limpiar}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              title="Limpiar"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button type="submit" className="btn btn-primary" disabled={query.trim().length < 2}>
          Buscar
        </button>
      </form>

      <div style={{ marginTop: 20 }}>
        {q.isLoading && submitted && <TableSkeleton rows={6} />}
        {q.isError && (
          <p className="status status-error">No pudimos buscar. Reintentá en unos segundos.</p>
        )}
        {q.data && q.data.matches.length === 0 && (
          <p className="text-element-mid">Sin resultados para "{submitted}".</p>
        )}
        {q.data && q.data.matches.length > 0 && (
          <ResultadosTable
            matches={q.data.matches}
            truncado={q.data.truncado}
            onAbrir={abrir}
          />
        )}
      </div>

      {legajoParam && (
        <DetalleLegajoDrawer
          source={{ tipo: 'legajo', legajoId: legajoParam }}
          asociadoBuscadoId={asociadoParam}
          onClose={cerrar}
          onAbrirLegajo={abrirLegajoId}
        />
      )}
      {!legajoParam && padronParam && numeroParam && (
        <DetalleLegajoDrawer
          source={{ tipo: 'borrador', padronId: padronParam, numero: numeroParam }}
          asociadoBuscadoId={asociadoParam}
          onClose={cerrar}
          onAbrirLegajo={abrirLegajoId}
        />
      )}
    </section>
  );
}

// ────────── Tabla resultados ──────────

function ResultadosTable(props: {
  matches: BusquedaMatchItem[];
  truncado: boolean;
  onAbrir: (m: BusquedaMatchItem) => void;
}) {
  const { matches, truncado, onAbrir } = props;
  const esChico = useMediaQuery('(max-width: 760px)');
  return (
    <>
      <p className="text-element-mid" style={{ fontSize: 13 }}>
        {matches.length} {matches.length === 1 ? 'resultado' : 'resultados'}
        {truncado && ' (resultados truncados, refiná la búsqueda)'}
      </p>
      {esChico ? (
        <ResultadosCards matches={matches} onAbrir={onAbrir} />
      ) : (
        <div className="tableWrap" style={{ marginTop: 8 }}>
          <table className="dataTable">
            <thead>
              <tr>
                <th>DNI</th>
                <th>ID Web</th>
                <th>Nombre</th>
                <th>Parentesco</th>
                <th>Titular</th>
                <th>Legajo</th>
                <th>Padrón</th>
                <th>Empresa</th>
                <th>Estado persona</th>
                <th>Estado legajo</th>
                <th>Plan real</th>
                <th>Pago</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => (
                <tr key={m.asociadoId} onClick={() => onAbrir(m)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontFamily: 'monospace' }}>{m.documento}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-element-mid)' }}>{m.asociadoId}</td>
                  <td>
                    {m.apellSolt} {m.nombres}
                  </td>
                  <td>{PARENTESCO_LABEL[m.parentesco] ?? m.parentesco}</td>
                  <td>{m.matchTipo === 'titular' ? '—' : m.titularNombre}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{m.legajoNumero}</td>
                  <td style={{ fontSize: 12 }}>{m.padronNombre}</td>
                  <td style={{ fontSize: 12 }}>{m.empresaNombre ?? '—'}</td>
                  <td>
                    {m.estadoPersona ? (
                      <EstadoPersonaBadge estado={m.estadoPersona} motivo={m.bajaMotivo} vigencia={m.bajaVigencia} />
                    ) : (
                      <span style={{ color: 'var(--color-element-mid)' }}>—</span>
                    )}
                  </td>
                  <td>
                    <EstadoLegajoBadge estado={m.legajoEstado} />
                  </td>
                  <td>
                    <PlanBinaryBadge ms2Alta={m.ms2Alta} />
                  </td>
                  <td>
                    <PagoBadge estado={m.pagoEstado} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * Vista de resultados para pantallas chicas: cada resultado es una tarjeta
 * tocable con los datos priorizados (nombre + estado arriba; DNI, legajo,
 * parentesco y padrón debajo). Evita el scroll horizontal de una tabla de 8
 * columnas en mobile.
 */
function ResultadosCards(props: {
  matches: BusquedaMatchItem[];
  onAbrir: (m: BusquedaMatchItem) => void;
}) {
  const { matches, onAbrir } = props;
  return (
    <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
      {matches.map((m) => (
        <button
          key={m.asociadoId}
          type="button"
          onClick={() => onAbrir(m)}
          style={{
            textAlign: 'left',
            width: '100%',
            background: 'var(--color-background)',
            border: '1px solid var(--color-outline)',
            borderRadius: 'var(--radius-md)',
            padding: '12px 14px',
            cursor: 'pointer',
            font: 'inherit',
            color: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div className="rowBetween" style={{ alignItems: 'flex-start', gap: 8 }}>
            <strong style={{ fontSize: 14 }}>
              {m.apellSolt} {m.nombres}
            </strong>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {m.estadoPersona && <EstadoPersonaBadge estado={m.estadoPersona} motivo={m.bajaMotivo} vigencia={m.bajaVigencia} />}
              <EstadoLegajoBadge estado={m.legajoEstado} />
              {m.ms2Alta && <PlanBinaryBadge ms2Alta={m.ms2Alta} />}
              {m.pagoEstado && <PagoBadge estado={m.pagoEstado} />}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '2px 14px',
              fontSize: 12,
              color: 'var(--color-element-mid)',
            }}
          >
            <span>
              DNI <strong style={{ fontFamily: 'monospace' }}>{m.documento}</strong>
            </span>
            <span>
              ID Web <strong style={{ fontFamily: 'monospace' }}>{m.asociadoId}</strong>
            </span>
            <span>
              Legajo <strong style={{ fontFamily: 'monospace' }}>{m.legajoNumero}</strong>
            </span>
            <span>
              {PARENTESCO_LABEL[m.parentesco] ?? m.parentesco}
              {m.matchTipo !== 'titular' && m.titularNombre ? ` · titular ${m.titularNombre}` : ''}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-element-mid)' }}>
            {m.padronNombre}
            {m.empresaNombre ? ` · ${m.empresaNombre}` : ''}
          </div>
        </button>
      ))}
    </div>
  );
}

// ────────── Drawer de detalle ──────────

export type DetalleSource =
  | { tipo: 'legajo'; legajoId: string }
  | { tipo: 'borrador'; padronId: string; numero: string };

export function DetalleLegajoDrawer(props: {
  source: DetalleSource;
  asociadoBuscadoId?: string;
  onClose: () => void;
  onAbrirLegajo: (legajoId: string) => void;
}) {
  const { source, asociadoBuscadoId, onClose, onAbrirLegajo } = props;
  const q = useQuery({
    queryKey:
      source.tipo === 'legajo'
        ? ['busqueda-detalle', source.legajoId]
        : ['busqueda-detalle-borrador', source.padronId, source.numero],
    queryFn: () =>
      source.tipo === 'legajo'
        ? getLegajoDetalle(source.legajoId)
        : getGrupoBorradorDetalle(source.padronId, source.numero),
  });

  // VEN-540: al abrir la ficha de un legajo dado de alta, el portal va a Binary
  // y se acomoda (Binary manda). El server frena si ya corrió hace poco; acá
  // se evita el POST cuando la corrida guardada es reciente. El resultado se
  // muestra en la sección "Sincronización con Binary" y la ficha se refresca.
  const queryClient = useQueryClient();
  const legajoId = source.tipo === 'legajo' ? source.legajoId : null;
  const estadoLegajo = q.data?.legajo.estado;
  const ultimaSyncAt = q.data?.legajo.binarySync?.at;
  const syncMut = useMutation({
    mutationFn: (forzar: boolean) => sincronizarLegajoBinary(legajoId ?? '', forzar),
    // Sin condicionar a `accion === 'corrio'`: con `en_curso` otro job ya está
    // tocando el legajo, y `reciente` significa que lo tocó hace un rato — en
    // los dos casos la ficha puede estar vieja.
    onSuccess: () => refrescarBusqueda(queryClient, legajoId),
  });
  const { mutate: dispararSync } = syncMut;
  const disparadoPara = useRef<string | null>(null);
  useEffect(() => {
    if (!legajoId || estadoLegajo !== 'dado_de_alta') return;
    if (disparadoPara.current === legajoId || syncEsReciente(ultimaSyncAt)) return;
    disparadoPara.current = legajoId;
    dispararSync(false);
  }, [legajoId, estadoLegajo, ultimaSyncAt, dispararSync]);
  const syncUi: SyncUi | undefined = legajoId
    ? {
        corriendo: syncMut.isPending,
        respuesta: syncMut.data,
        error: syncMut.isError,
        forzar: () => dispararSync(true),
      }
    : undefined;

  return (
    <ModalOverlay onClose={onClose}>
      <div
        className="card fichaCard"
        role="dialog"
        aria-modal="true"
        style={{
          width: 'min(900px, 100%)',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <header className="rowBetween" style={{ alignItems: 'baseline' }}>
          <div>
            <h3
              className="heading-5"
              style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
            >
              {q.data ? (
                <>
                  <span>Legajo {q.data.legajo.numero}</span>
                  <EstadoLegajoBadge estado={q.data.legajo.estado} size={12} />
                </>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <Spinner size={18} />
                  <span className="text-element-mid" style={{ fontWeight: 500 }}>
                    Cargando legajo
                  </span>
                </span>
              )}
            </h3>
            {q.data && (
              <p className="text-element-mid" style={{ fontSize: 12, marginTop: 4 }}>
                Padrón <strong style={{ fontWeight: 600 }}>{q.data.padron.nombreArchivo}</strong>
                {' · '}Empresa{' '}
                <strong style={{ fontWeight: 600 }}>{q.data.empresa.nombre ?? q.data.empresa.id}</strong>
              </p>
            )}
          </div>
          <button type="button" className="btn btn-ghost btn--sm" onClick={onClose}>
            Cerrar
          </button>
        </header>

        <div style={{ overflow: 'auto', flex: 1, paddingRight: 8 }}>
          {q.isLoading && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: '48px 0',
                color: 'var(--color-element-mid)',
              }}
            >
              <Spinner size={32} />
              <span style={{ fontSize: 13 }}>Trayendo los datos del legajo…</span>
            </div>
          )}
          {q.isError && (
            <p className="status status-error">
              No pudimos cargar el detalle. Reintentá.
            </p>
          )}
          {q.data && (
            <DetalleContent
              detalle={q.data}
              asociadoBuscadoId={asociadoBuscadoId}
              onAbrirLegajo={onAbrirLegajo}
              sync={syncUi}
            />
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

function DetalleContent(props: {
  detalle: LegajoDetalle;
  asociadoBuscadoId?: string;
  onAbrirLegajo: (legajoId: string) => void;
  sync?: SyncUi;
}) {
  const { detalle, asociadoBuscadoId, onAbrirLegajo, sync } = props;
  const hermanos = detalle.legajosHermanos ?? [];
  // Personas que la última sincronización con Binary tocó (para el chip de la card).
  const ultimaSync = sync?.respuesta?.sync ?? detalle.legajo.binarySync ?? null;
  const tocadosPorSync = new Set(
    ultimaSync && !ultimaSync.simulado
      ? [
          ...ultimaSync.cambios,
          ...ultimaSync.altas,
          // Vincular (guardar el afi_id) no cambia nada visible: no lleva el chip.
          ...ultimaSync.marcas.filter((m) => m.tipo !== 'vinculado'),
        ].map((x) => x.asociadoId)
      : [],
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Aviso: otros legajos con el mismo número (bis + real conviviendo) */}
      {hermanos.length > 0 && (
        <div
          style={{
            border: '1px solid #f0c869',
            background: '#fffaf0',
            borderRadius: 10,
            padding: '6px 12px',
            display: 'grid',
            gap: 2,
          }}
        >
          {hermanos.map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => onAbrirLegajo(h.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                padding: '6px 0',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              <span style={{ color: '#8a5a00', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {hermanos.length === 1
                  ? 'Este número tiene otro legajo:'
                  : 'Otro legajo con este número:'}
              </span>
              <EstadoLegajoBadge estado={h.estado} size={11} />
              <span>
                Titular <strong>{h.titularNombre || '—'}</strong>
                {h.titularDocumento && (
                  <span style={{ color: 'var(--color-element-mid)' }}> · DNI {h.titularDocumento}</span>
                )}
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 12,
                  color: 'var(--color-primary)',
                  whiteSpace: 'nowrap',
                }}
              >
                Ver →
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Oferta MS2 (TMK / Medicus editores) */}
      <OfertaMs2Section detalle={detalle} />

      {/* Reportar interesado en cambio a MS2 → rueda de SkyTel (Medicus) */}
      <ReportarInteresadoSkytelSection detalle={detalle} />

      {/* Resumen estado del legajo */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
        <Info label="Estado padrón" value={detalle.padron.estado} />
        <Info
          label="Plan superador"
          value={
            detalle.legajo.planSuperadorElegido === true
              ? 'Sí'
              : detalle.legajo.planSuperadorElegido === false
                ? 'No'
                : 'Sin elegir'
          }
        />
        <Info
          label="Activación"
          value={
            detalle.legajo.activacionCompletadaAt
              ? fmtFecha(detalle.legajo.activacionCompletadaAt)
              : 'Sin activar'
          }
        />
      </section>

      {/* Estado real en Binary (plan + alta + pago) — solo roles Medicus */}
      <EstadoBinarySection detalle={detalle} />

      {/* Sincronización Binary → portal al abrir la ficha (VEN-540) */}
      <SincronizacionBinarySection detalle={detalle} sync={sync} />

      {/* Titular */}
      <section>
        <h4 className="heading-6" style={{ marginBottom: 8 }}>
          Titular
        </h4>
        <AsociadoCard
          asociado={detalle.titular}
          destacado={detalle.titular.id === asociadoBuscadoId}
          actualizadoDesdeBinary={tocadosPorSync.has(detalle.titular.id)}
        />
      </section>

      {/* Familiares */}
      {detalle.familiares.length > 0 && (
        <section>
          <h4 className="heading-6" style={{ marginBottom: 8 }}>
            Familiares ({detalle.familiares.length})
          </h4>
          <div style={{ display: 'grid', gap: 8 }}>
            {detalle.familiares.map((f) => (
              <AsociadoCard
                key={f.id}
                asociado={f}
                destacado={f.id === asociadoBuscadoId}
                actualizadoDesdeBinary={tocadosPorSync.has(f.id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Observaciones */}
      {detalle.observaciones.length > 0 && (
        <section>
          <h4 className="heading-6" style={{ marginBottom: 8 }}>
            Observaciones ({detalle.observaciones.length})
          </h4>
          <div style={{ display: 'grid', gap: 8 }}>
            {detalle.observaciones.map((o, idx) => (
              <ObservacionCard
                key={`${o.asociadoId}-${o.campo}-${idx}`}
                observacion={o}
                asociadoNombre={nombreAsociadoEnLegajo(detalle, o.asociadoId)}
              />
            ))}
          </div>
        </section>
      )}

      {/* IDs técnicos (al final) */}
      <section
        style={{
          fontSize: 11,
          color: 'var(--color-element-mid)',
          fontFamily: 'monospace',
          borderTop: '1px solid var(--color-outline)',
          paddingTop: 10,
        }}
      >
        {detalle.legajo.id
          ? `legajo_id: ${detalle.legajo.id}`
          : `padron_id: ${detalle.padron.id} · sin legajo (padrón en borrador)`}
        {detalle.legajo.binaryAfiId && (
          <>
            <br />binary_afi_id: {detalle.legajo.binaryAfiId}
          </>
        )}
      </section>
    </div>
  );
}

// ────────── Estado real en Binary (plan + alta + pago) ──────────

/** Cruces que detectan inconsistencias / demoras entre Binary y el onboarding. */
function alertasBinary(legajo: LegajoDetalle['legajo']): string[] {
  const { ms2Alta, pago, estado } = legajo;
  const pagado = pago?.estado === 'aprobado';
  const out: string[] = [];
  if (pagado && ms2Alta?.afiliadoActivo && ms2Alta.plan === 'MS1') {
    out.push('Pagó el MS2 pero todavía figura en MS1 en Binary (demora del cambio de plan).');
  }
  if (pagado && ms2Alta && !ms2Alta.afiliadoActivo) {
    out.push('Tiene pago MS2 aprobado pero no figura como afiliado activo en Binary (alta sin completar).');
  }
  if (estado === 'dado_de_alta' && ms2Alta && !ms2Alta.afiliadoActivo) {
    out.push('Marcado "dado de alta" en el micrositio pero no figura como afiliado activo en Binary.');
  }
  return out;
}

function labelAfiliado(ms2Alta: Ms2AltaResumen | null | undefined): string {
  if (!ms2Alta) return '—';
  if (!ms2Alta.afiliadoActivo) return 'No activo';
  return `Activo${ms2Alta.estadoAfi ? ` (${ms2Alta.estadoAfi})` : ''}`;
}

function labelPlanElegido(elegido: boolean | null | undefined): string {
  if (elegido === true) return 'MS2';
  if (elegido === false) return 'MS1';
  return 'Sin elegir';
}

/**
 * Muestra el estado REAL del legajo en Binary/PMED (plan vigente, si está
 * dado de alta como afiliado activo, medicard, fecha de alta) y el pago MS2,
 * cruzado contra el estado del onboarding en Mongo. Visible para todos los
 * roles (se reusa también en la planilla del TMK).
 */
function EstadoBinarySection({ detalle }: { detalle: LegajoDetalle }) {
  const { ms2Alta, pago, binaryMedicard, binaryAltaAt } = detalle.legajo;
  // Si el legajo todavía no se sincronizó con Binary y no hay pago, no aporta nada.
  if (!ms2Alta && !pago) return null;
  const alertas = alertasBinary(detalle.legajo);
  const fechaAlta = ms2Alta?.fechaAltaBinary ?? (binaryAltaAt ? binaryAltaAt.slice(0, 10) : null);

  return (
    <section
      style={{
        padding: '14px 16px',
        background: '#f8fafc',
        border: '1px solid var(--color-outline)',
        borderRadius: 12,
      }}
    >
      <h4 className="heading-6" style={{ margin: '0 0 10px' }}>
        Estado real en Binary (MS2)
      </h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
        <Info label="Plan en Binary" value={<PlanBinaryBadge ms2Alta={ms2Alta} />} />
        <Info label="Afiliado" value={labelAfiliado(ms2Alta)} />
        <Info label="Alta en Binary" value={fechaAlta ? fmtFecha(fechaAlta) : 'Sin fecha'} />
        <Info label="Medicard" value={binaryMedicard ?? '—'} />
        {pago ? (
          <>
            <Info label="Pago MS2" value={<PagoBadge estado={pago.estado} />} />
            <Info label="Monto" value={pago.monto ? fmtMoney(pago.monto) : '—'} />
            <Info
              label="Fecha pago"
              value={pago.actualizadoAt ? fmtFecha(pago.actualizadoAt) : '—'}
            />
          </>
        ) : (
          <Info label="Pago MS2" value="Sin registrar" />
        )}
        <Info
          label="Plan elegido (wizard)"
          value={labelPlanElegido(detalle.legajo.planSuperadorElegido)}
        />
      </div>

      {alertas.map((a, i) => (
        <div
          key={i}
          style={{
            marginTop: 10,
            padding: '8px 12px',
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.35)',
            borderRadius: 8,
            fontSize: 12,
            color: '#92400e',
          }}
        >
          ⚠ {a}
        </div>
      ))}

      {ms2Alta?.syncedAt && (
        <p style={{ fontSize: 10, color: 'var(--color-element-mid)', margin: '10px 0 0' }}>
          Sincronizado con Binary el {fmtFechaHora(ms2Alta.syncedAt)}.
          {pago?.conciliadoBinary && ' · Pago conciliado contra el gateway.'}
        </p>
      )}
    </section>
  );
}

// ────────── Sincronización con Binary (VEN-540) ──────────

/** Estado de la corrida que el drawer dispara al abrir la ficha. */
export interface SyncUi {
  corriendo: boolean;
  respuesta?: RespuestaSync;
  error: boolean;
  forzar: () => void;
}

/** Resultados de una corrida que sí comparó los datos (los demás son estados). */
const RESULTADOS_CON_RESUMEN = new Set(['sincronizado', 'sin_cambios', 'simulado']);

const RESULTADO_SYNC_LABEL: Record<string, string> = {
  sincronizado: 'Sincronizado desde Binary',
  sin_cambios: 'Sin diferencias con Binary',
  simulado: 'Simulación: se registró qué cambiaría, sin aplicar',
  binary_no_disponible: 'Binary no disponible',
  titular_no_en_binary: 'El titular no está en Binary',
  titular_alta_pendiente: 'El titular tiene un alta en camino en Binary',
  error: 'Error al sincronizar',
};

function haceCuanto(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (Number.isNaN(min)) return '';
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `hace ${h} h`;
  return `el ${fmtFecha(iso)}`;
}

type TonoSync = 'ok' | 'warn' | 'error' | 'cargando' | 'neutral';

/** Qué decir arriba de todo: primero lo que está pasando ahora, después la última corrida. */
function cabeceraSync(
  noAplica: boolean,
  sync: SyncUi | undefined,
  ultima: LegajoDetalle['legajo']['binarySync'],
): { texto: string; clase: string; tono: TonoSync } {
  if (noAplica) {
    return {
      texto: 'No aplica: solo los legajos dados de alta se sincronizan con Binary.',
      clase: 'text-element-mid',
      tono: 'neutral',
    };
  }
  if (sync?.corriendo) {
    return { texto: 'Consultando Binary…', clase: 'text-element-mid', tono: 'cargando' };
  }
  if (sync?.error) {
    return {
      texto: 'No se pudo consultar Binary. Reintentá.',
      clase: 'status status-error',
      tono: 'error',
    };
  }
  const accion = sync?.respuesta?.accion;
  if (accion === 'en_curso') {
    return {
      texto: 'Otra sincronización de este legajo está en curso.',
      clase: 'text-element-mid',
      tono: 'cargando',
    };
  }
  if (accion === 'pausado') {
    return {
      texto: 'La sincronización con Binary está pausada.',
      clase: 'text-element-mid',
      tono: 'neutral',
    };
  }
  if (!ultima) {
    return { texto: 'Todavía no se sincronizó con Binary.', clase: 'text-element-mid', tono: 'neutral' };
  }
  const ok = ultima.resultado === 'sincronizado' || ultima.resultado === 'sin_cambios';
  const malo = ultima.resultado === 'binary_no_disponible' || ultima.resultado === 'error';
  return {
    texto: RESULTADO_SYNC_LABEL[ultima.resultado] ?? ultima.resultado,
    clase: ok ? '' : malo ? 'status status-error' : 'status status-warning',
    tono: ok ? 'ok' : malo ? 'error' : 'warn',
  };
}

/** Icono del estado de la sincronización, al lado del título. */
function IconoSync({ tono }: { tono: TonoSync }) {
  if (tono === 'cargando') return <Spinner size={14} />;
  if (tono === 'ok') return <CheckCircle2 size={18} color="#15803d" aria-hidden="true" />;
  if (tono === 'warn') return <AlertTriangle size={18} color="#b45309" aria-hidden="true" />;
  if (tono === 'error') return <XCircle size={18} color="#b91c1c" aria-hidden="true" />;
  return null;
}

export function SincronizacionBinarySection({
  detalle,
  sync,
}: {
  detalle: LegajoDetalle;
  sync?: SyncUi;
}) {
  const [verHistorial, setVerHistorial] = useState(false);
  const { user } = useAuthStore();
  const puedeVerHistorial = ROLES_HISTORIAL_SYNC.has(user?.rol ?? '');
  // Un grupo de padrón en borrador no tiene legajo: no hay nada que sincronizar.
  if (!detalle.legajo.id) return null;
  const noAplica = detalle.legajo.estado !== 'dado_de_alta';
  const s = sync?.respuesta?.sync ?? detalle.legajo.binarySync ?? null;
  const cabecera = cabeceraSync(noAplica, sync, s);
  // El resumen en palabras solo tiene sentido si la corrida llegó a comparar.
  const conResumen = Boolean(s && !noAplica && RESULTADOS_CON_RESUMEN.has(s.resultado));
  const resumen =
    s && conResumen
      ? resumirSync(
          s,
          [detalle.titular, ...detalle.familiares].map((p) => ({
            id: p.id,
            nombre: `${p.apellSolt} ${p.nombres}`.trim(),
          })),
        )
      : null;
  // Mientras consulta o si falló, la línea de estado va igual (arriba del último resumen).
  const mostrarEstado = !resumen || Boolean(sync?.corriendo || sync?.error);

  return (
    <section
      style={{
        padding: '14px 16px',
        background: '#f8fafc',
        border: '1px solid var(--color-outline)',
        borderRadius: 12,
      }}
    >
      <div className="rowBetween" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h4
          className="heading-6"
          style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <IconoSync tono={cabecera.tono} />
          Sincronización con Binary
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {s && !noAplica && puedeVerHistorial && (
            <button
              type="button"
              className="btn btn-ghost btn--sm"
              onClick={() => setVerHistorial((v) => !v)}
            >
              {verHistorial ? 'Ocultar historial' : 'Ver historial'}
            </button>
          )}
          {sync && !noAplica && (
            <button
              type="button"
              className="btn btn-ghost btn--sm"
              onClick={sync.forzar}
              disabled={sync.corriendo}
            >
              {sync.corriendo ? 'Consultando…' : 'Actualizar desde Binary'}
            </button>
          )}
        </div>
      </div>
      {mostrarEstado && (
        <p
          className={cabecera.clase}
          style={{ margin: '8px 0 0', fontSize: 13, display: 'block', textTransform: 'none' }}
        >
          {cabecera.texto}
        </p>
      )}
      {s?.error && !noAplica && (
        <p style={{ fontSize: 12, margin: '6px 0 0', color: '#92400e' }}>{s.error}</p>
      )}

      {resumen && s && (
        <>
          <p style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 600 }}>{resumen.frase}</p>
          <p className="text-element-mid" style={{ fontSize: 11, margin: '2px 0 0' }}>
            Consultado {haceCuanto(s.at)} por {s.por}.
          </p>

          {resumen.personas.length > 0 && (
            <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
              {resumen.personas.map((p) => (
                <div key={p.clave} style={{ fontSize: 13 }}>
                  <strong>{p.nombre}</strong>
                  <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                    {p.lineas.map((l, i) => (
                      <li key={`${i}-${l}`}>{l}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          {resumen.personas.length > 0 && resumen.sinCambios.length > 0 && (
            <p className="text-element-mid" style={{ fontSize: 12, margin: '10px 0 0' }}>
              Sin cambios: {resumen.sinCambios.join(', ')}.
            </p>
          )}
          {resumen.vinculadas > 0 && (
            <p className="text-element-mid" style={{ fontSize: 12, margin: '6px 0 0' }}>
              {textoVinculadas(resumen.vinculadas, s.simulado)}
            </p>
          )}
        </>
      )}

      {s && s.divergencias.length > 0 && (
        <div
          style={{
            marginTop: 10,
            padding: '8px 12px',
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.35)',
            borderRadius: 8,
            fontSize: 12,
            color: '#92400e',
          }}
        >
          <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
            Diferencias de grupo con Binary (no se aplican solas)
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {s.divergencias.map((d, i) => (
              <li key={`${d.tipo}-${d.afiId ?? d.asociadoId ?? i}`} style={{ marginBottom: 4 }}>
                {d.detalle}
                {d.aplicada ? (
                  <strong style={{ color: '#15803d' }}> Aplicado en esta sincronización.</strong>
                ) : (
                  d.propuesta && (
                    <>
                      {' '}
                      <em>Propuesta: {d.propuesta}</em>
                    </>
                  )
                )}
                {d.accion === 'retitularizar' && !d.aplicada && !s.simulado && (
                  <RetitularizarBoton
                    detalle={detalle}
                    divergencia={d}
                    onHecho={() => sync?.forzar()}
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {verHistorial && puedeVerHistorial && <HistorialSync legajoId={detalle.legajo.id} />}
    </section>
  );
}

/**
 * Retitularizar el legajo con el familiar que Binary tiene como cabeza. Solo
 * admins, y con confirmación: del titular cuelgan el pago MS2, HubSpot, SkyTel
 * y las ofertas, así que nunca se hace solo.
 */
function RetitularizarBoton(props: {
  detalle: LegajoDetalle;
  divergencia: Divergencia;
  onHecho: () => void;
}) {
  const { detalle, divergencia, onHecho } = props;
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);
  const nuevoId = divergencia.nuevoTitularAsociadoId ?? '';
  const mutation = useMutation({
    mutationFn: () => retitularizarLegajo(detalle.legajo.id, nuevoId),
    onSuccess: () => {
      setConfirmando(false);
      refrescarBusqueda(queryClient, detalle.legajo.id);
      onHecho();
    },
  });
  const esAdmin =
    user?.rol === UserRole.SUPERADMIN_MEDICUS || user?.rol === UserRole.ADMIN_MEDICUS;
  if (!esAdmin || !nuevoId) return null;
  const nuevo = detalle.familiares.find((f) => f.id === nuevoId);
  const nombreNuevo = nuevo ? `${nuevo.apellSolt} ${nuevo.nombres}`.trim() : 'el familiar';
  const nombreActual = `${detalle.titular.apellSolt} ${detalle.titular.nombres}`.trim();

  if (mutation.isSuccess) {
    return (
      <div style={{ marginTop: 6, color: '#15803d' }}>
        ✓ Legajo retitularizado con {mutation.data.titularNuevo.nombre}.
        {mutation.data.avisos.length > 0 && (
          <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: '#92400e' }}>
            {mutation.data.avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 6 }}>
      {!confirmando ? (
        <button type="button" className="btn btn-ghost btn--sm" onClick={() => setConfirmando(true)}>
          Retitularizar con {nombreNuevo}
        </button>
      ) : (
        <div
          style={{
            padding: '8px 10px',
            background: '#fff',
            border: '1px solid rgba(245,158,11,0.45)',
            borderRadius: 8,
          }}
        >
          <p style={{ margin: '0 0 8px' }}>
            <strong>{nombreNuevo}</strong> pasa a ser titular del legajo {detalle.legajo.numero} y{' '}
            <strong>{nombreActual}</strong> queda como familiar. El pago MS2, la oferta, SkyTel y
            HubSpot que estén a nombre del titular anterior no se reescriben: se listan como avisos
            para revisarlos.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary btn--sm"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? 'Retitularizando…' : 'Confirmar'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn--sm"
              disabled={mutation.isPending}
              onClick={() => setConfirmando(false)}
            >
              Cancelar
            </button>
          </div>
          {mutation.isError && (
            <p style={{ margin: '8px 0 0', color: '#b91c1c' }}>
              {msgErrorApi(mutation.error, 'No se pudo retitularizar. Reintentá.')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Quién ve el historial de corridas: superadmins y el equipo Medicus de
 * consulta (USER_MEDICUS, el rol de ABM). El server aplica la misma regla.
 */
const ROLES_HISTORIAL_SYNC: ReadonlySet<string> = new Set([
  UserRole.SUPERADMIN_MEDICUS,
  UserRole.USER_MEDICUS,
]);

function HistorialSync({ legajoId }: { legajoId: string }) {
  const q = useQuery({
    queryKey: ['sync-binary-historial', legajoId],
    queryFn: () => historialSyncBinary(legajoId),
  });
  if (q.isLoading) return <LinesSkeleton lines={2} />;
  if (q.isError) {
    return (
      <p className="status status-error" style={{ display: 'block', marginTop: 8 }}>
        No se pudo cargar el historial.
      </p>
    );
  }
  const items = q.data ?? [];
  if (items.length === 0) {
    return (
      <p className="text-element-mid" style={{ fontSize: 12, marginTop: 8 }}>
        Sin corridas registradas.
      </p>
    );
  }
  return (
    <div
      style={{
        marginTop: 12,
        borderTop: '1px solid var(--color-outline)',
        paddingTop: 10,
        display: 'grid',
        gap: 12,
      }}
    >
      <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Historial de cambios</p>
      {items.map((e) => (
        <CorridaHistorial key={e.id ?? e._id ?? e.at} corrida={e} />
      ))}
    </div>
  );
}

/** Una corrida del historial: cuándo, quién, la frase y qué se le cambió a cada persona. */
function CorridaHistorial({ corrida: e }: { corrida: SyncBinaryEvento }) {
  const conResumen = RESULTADOS_CON_RESUMEN.has(e.resultado);
  const resumen = conResumen ? resumirSync(e, []) : null;
  return (
    <div style={{ fontSize: 12 }}>
      <div className="text-element-mid" style={{ fontSize: 11 }}>
        {fmtFechaHora(e.at)} · por {e.por}
      </div>
      <div style={{ fontWeight: 600, marginTop: 2 }}>
        {resumen ? resumen.frase : (RESULTADO_SYNC_LABEL[e.resultado] ?? e.resultado)}
      </div>
      {!resumen && e.error && <div style={{ color: '#92400e' }}>{e.error}</div>}
      {resumen?.personas.map((p) => (
        <div key={p.clave} style={{ marginTop: 4 }}>
          <strong>{p.nombre}</strong>
          <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
            {p.lineas.map((l, i) => (
              <li key={`${i}-${l}`}>{l}</li>
            ))}
          </ul>
        </div>
      ))}
      {resumen && resumen.vinculadas > 0 && (
        <div className="text-element-mid" style={{ marginTop: 4 }}>
          {textoVinculadas(resumen.vinculadas, e.simulado)}
        </div>
      )}
      {e.divergencias.length > 0 && (
        <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: '#92400e' }}>
          {e.divergencias.map((d, i) => (
            <li key={`${d.tipo}-${i}`}>{d.detalle}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ────────── Sub-componentes ──────────

function Info(props: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '8px 10px',
        background: 'var(--color-bg-subtle, #f5f7fa)',
        borderRadius: 6,
        fontSize: 13,
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--color-element-mid)', marginBottom: 2 }}>
        {props.label}
      </div>
      <strong>{props.value}</strong>
    </div>
  );
}

function AsociadoCard(props: {
  asociado: LegajoDetalleAsociado;
  destacado: boolean;
  /** La última sincronización con Binary escribió en esta persona. */
  actualizadoDesdeBinary?: boolean;
}) {
  const { asociado: a, destacado, actualizadoDesdeBinary } = props;
  return (
    <article
      style={{
        padding: '12px 14px',
        borderRadius: 'var(--radius-md)',
        border: destacado
          ? '2px solid var(--color-primary-darker)'
          : '1px solid var(--color-outline)',
        background: destacado ? 'rgba(0,37,100,0.04)' : 'var(--color-background)',
      }}
    >
      <header className="rowBetween" style={{ alignItems: 'baseline', marginBottom: 6 }}>
        <strong style={{ fontSize: 14 }}>
          {a.apellSolt} {a.nombres}
          {destacado && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                background: 'var(--color-primary-darker)',
                color: 'white',
                padding: '2px 8px',
                borderRadius: 12,
              }}
            >
              persona buscada
            </span>
          )}
          {actualizadoDesdeBinary && (
            <span
              className="status status-info"
              style={{ marginLeft: 8, fontSize: 11, textTransform: 'none' }}
              title={
                a.sincronizadoBinaryAt
                  ? `Actualizado desde Binary el ${fmtFechaHora(a.sincronizadoBinaryAt)}`
                  : 'Actualizado desde Binary'
              }
            >
              actualizado desde Binary
            </span>
          )}
          {a.estadoPersona && a.estadoPersona !== 'activa' && (
            <span style={{ marginLeft: 8 }}>
              <EstadoPersonaBadge
                estado={a.estadoPersona}
                motivo={
                  a.bajaMotivo ?? a.bajaBinaryMotivo ?? a.noEnBinaryMotivo ?? a.bloqueadoMotivo
                }
                vigencia={a.bajaVigencia}
              />
            </span>
          )}
        </strong>
        <span style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>
          {PARENTESCO_LABEL[a.parentesco] ?? a.parentesco}
        </span>
      </header>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 6,
          fontSize: 12,
        }}
      >
        <KV k="DNI" v={a.documento} mono />
        <KV k="ID Web" v={a.id} mono />
        <KV k="CUIL" v={a.cuil ?? '—'} mono />
        <KV k="Fecha nac." v={a.fechaNacimiento ?? '—'} />
        <KV k="Sexo" v={a.sexo ?? '—'} />
        <KV k="Estado civil" v={a.estadoCivil ?? '—'} />
        <KV k="Nacionalidad" v={a.nacionalidad ?? '—'} />
        <KV
          k="Domicilio"
          v={
            a.domicilio
              ? `${a.domicilio} ${a.numero ?? ''}${a.piso ? ' P:' + a.piso : ''}${a.depto ? ' D:' + a.depto : ''}`.trim()
              : '—'
          }
        />
        <KV k="CP" v={a.cp ?? '—'} />
        <KV k="Email" v={a.email ?? '—'} />
        <KV
          k="Celular"
          v={a.areaCelular && a.numeroCelular ? `${a.areaCelular} ${a.numeroCelular}` : '—'}
          mono
        />
        {a.renaperEstado && <KV k="Renaper" v={a.renaperEstado} />}
      </div>
    </article>
  );
}

function KV(props: { k: string; v: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--color-element-mid)' }}>{props.k}</div>
      <div style={{ fontFamily: props.mono ? 'monospace' : 'inherit' }}>{props.v}</div>
    </div>
  );
}

function ObservacionCard(props: {
  observacion: LegajoDetalleObservacion;
  asociadoNombre: string;
}) {
  const { observacion: o, asociadoNombre } = props;
  const campoLabel = CAMPO_LABEL[o.campo] ?? o.campo;
  return (
    <article
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${o.resuelta ? 'var(--color-outline)' : 'var(--color-warning, #f59e0b)'}`,
        background: o.resuelta ? 'var(--color-background)' : 'rgba(245,158,11,0.05)',
      }}
    >
      <header className="rowBetween" style={{ alignItems: 'baseline', marginBottom: 4 }}>
        <strong style={{ fontSize: 13 }}>
          {campoLabel}
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--color-element-mid)' }}>
            sobre {asociadoNombre}
          </span>
        </strong>
        <span
          style={{
            fontSize: 10,
            padding: '2px 8px',
            borderRadius: 10,
            background: o.resuelta ? '#e2e8f0' : 'rgba(245,158,11,0.2)',
            color: o.resuelta ? '#475569' : '#9a3412',
            fontWeight: 600,
          }}
        >
          {o.resuelta ? 'Resuelta' : 'Pendiente'}
        </span>
      </header>
      {o.valorPropuesto && (
        <div style={{ fontSize: 12 }}>
          Valor propuesto: <strong>{o.valorPropuesto}</strong>
        </div>
      )}
      {o.mensaje && (
        <div style={{ fontSize: 12, color: 'var(--color-element-mid)', marginTop: 4 }}>
          “{o.mensaje}”
        </div>
      )}
      <div style={{ fontSize: 10, color: 'var(--color-element-mid)', marginTop: 4 }}>
        Reportada: {fmtFechaHora(o.reportadoAt)}
      </div>
    </article>
  );
}

function ModalOverlay(props: { children: React.ReactNode; onClose: () => void }) {
  const { children, onClose } = props;
  return (
    <div
      className="fichaOverlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 100,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>
  );
}

function nombreAsociadoEnLegajo(d: LegajoDetalle, asociadoId: string): string {
  if (d.titular.id === asociadoId) return `${d.titular.apellSolt} ${d.titular.nombres}`.trim();
  const f = d.familiares.find((x) => x.id === asociadoId);
  return f ? `${f.apellSolt} ${f.nombres}`.trim() : 'integrante';
}

// ────────── Oferta MS2 (TMK + Medicus editores) ──────────

function OfertaMs2Section({ detalle }: { detalle: LegajoDetalle }) {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);
  const [enviadoOk, setEnviadoOk] = useState<{ email: string; expiraAt: string } | null>(null);

  // Solo el rol comercial (TMK) puede ENVIAR la oferta MS2 (decisión de
  // producto 06/2026). Alineado con el @Roles del endpoint /tmk/oferta/.../enviar.
  const puedeMandarOferta = user ? esTelemarketer(user.rol) : false;
  // Los roles Medicus ven la info comercial (monto MS2) pero sin el botón de
  // envío; USUARIO_EMPRESA no ve nada de esto.
  const puedeVerInfo = puedeMandarOferta || isMedicus(user?.rol ?? UserRole.USUARIO_EMPRESA);

  const mutation = useMutation({
    mutationFn: () => enviarOfertaTmk(detalle.legajo.id),
    onSuccess: (data) => {
      setEnviadoOk({ email: data.emailDestinatario, expiraAt: data.expiraAt });
      setConfirmando(false);
      refrescarBusqueda(queryClient, detalle.legajo.id);
    },
  });

  if (!puedeVerInfo) return null;
  const { infoComercial } = detalle;
  // No mostramos la sección MS2 si el legajo no es elegible para oferta: así
  // ningún rol (tampoco Medicus) ve una opción que no se puede accionar. Solo
  // aparece cuando tiene sentido ofrecer/gestionar MS2 (legajo validado por el
  // titular, sin MS2 ya aceptado ni pago aprobado).
  if (!infoComercial.elegibleParaOferta) return null;
  const oferta = infoComercial.ofertaTmkVigente;

  return (
    <section
      style={{
        padding: '16px 18px',
        background: 'linear-gradient(135deg, rgba(0,37,100,0.04) 0%, rgba(10,61,143,0.04) 100%)',
        border: '1px solid rgba(0,37,100,0.12)',
        borderRadius: 12,
      }}
    >
      <div className="rowBetween" style={{ alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h4 className="heading-6" style={{ margin: '0 0 4px', color: '#002564' }}>
            Plan Medicus 2
          </h4>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#002564', lineHeight: 1.1 }}>
            {fmtMoney(infoComercial.montoMs2)}
            <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
              / mes (sin IVA)
            </span>
          </div>
          <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0' }}>
            Calculado sobre {1 + detalle.familiares.length}{' '}
            {detalle.familiares.length === 0 ? 'integrante' : 'integrantes'} con la fórmula MS2 vigente.
          </p>
        </div>
        {puedeMandarOferta && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={Boolean(oferta?.yaAceptada)}
            onClick={() => setConfirmando(true)}
            style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}
            title={oferta?.yaAceptada ? 'El titular ya aceptó esta oferta.' : undefined}
          >
            <Mail size={16} />
            <span>Enviar oferta MS2</span>
          </button>
        )}
      </div>

      {oferta && (
        <div
          style={{
            marginTop: 12,
            padding: '8px 12px',
            background: 'white',
            borderRadius: 8,
            fontSize: 12,
            color: '#475569',
          }}
        >
          <strong>Oferta vigente:</strong> enviada por{' '}
          {oferta.generadoPor.nombre ?? oferta.generadoPor.email ?? 'un asesor'} el{' '}
          {fmtFechaHora(oferta.generadoAt)}, vence {fmtFechaHora(oferta.expiraAt)}.
          {oferta.yaAceptada && (
            <span style={{ color: '#15803d', fontWeight: 600 }}> · Aceptada por el titular.</span>
          )}
        </div>
      )}

      {enviadoOk && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            background: 'rgba(21, 128, 61, 0.1)',
            border: '1px solid rgba(21, 128, 61, 0.3)',
            borderRadius: 8,
            fontSize: 13,
            color: '#15803d',
          }}
        >
          ✓ Mail enviado a <strong>{enviadoOk.email}</strong>. La oferta vence el{' '}
          {fmtFechaHora(enviadoOk.expiraAt)}.
        </div>
      )}

      {confirmando && (
        <ConfirmarEnvioModal
          email={detalle.titular.email ?? '(sin email)'}
          monto={infoComercial.montoMs2}
          legajoNumero={detalle.legajo.numero}
          atribuidoA={oferta?.generadoPor}
          yoEmail={user?.email}
          onCancelar={() => setConfirmando(false)}
          onConfirmar={() => mutation.mutate()}
          enviando={mutation.isPending}
          error={
            mutation.isError
              ? ((mutation.error as { response?: { data?: { message?: string; error?: { message?: string } } } })
                  .response?.data?.error?.message ??
                (mutation.error as { response?: { data?: { message?: string } } }).response?.data?.message ??
                'No pudimos enviar el mail. Reintentá.')
              : null
          }
        />
      )}
    </section>
  );
}

// ────────── Reportar interesado → rueda de SkyTel (Medicus) ──────────

/** Mensaje de error de una respuesta de la API (axios), con fallback. */
function msgErrorApi(err: unknown, fallback: string): string {
  const data = (
    err as { response?: { data?: { message?: string; error?: { message?: string } } } }
  ).response?.data;
  return data?.error?.message ?? data?.message ?? fallback;
}

function ReportarInteresadoSkytelSection({ detalle }: { detalle: LegajoDetalle }) {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);
  const [resultado, setResultado] = useState<'generado' | 'ya_estaba' | null>(null);
  const [confirmarOferta, setConfirmarOferta] = useState(false);
  const [ofertaEnviada, setOfertaEnviada] = useState<{ email: string } | null>(null);
  const [linkCopiado, setLinkCopiado] = useState<string | null>(null);
  // Cartel de atribución antes de copiar el link: la venta pasa a ser de quien
  // lo copia, aunque el link vigente lo haya generado otro agente.
  const [avisoLink, setAvisoLink] = useState(false);

  // Equipo Medicus (incluye solo-lectura) + admins — mismo scope que el endpoint.
  const puede = isMedicus(user?.rol ?? UserRole.USUARIO_EMPRESA);
  // Aplica a TODO socio en estado ofertable —INCLUIDO `dado_de_alta`, el grueso
  // de los que piden cambiar a MS2— mientras no haya elegido MS2 todavía. NO
  // usamos `infoComercial.elegibleParaOferta`: esa es más estricta (solo
  // `validado_por_titular`, para el ENVÍO de oferta) y escondía el botón justo
  // para los dados de alta. El backend revalida pago aprobado y teléfono.
  const ofertable = [
    'validado_por_titular',
    'en_alta_binary',
    'dado_de_alta',
    'rechazado_binary',
  ].includes(detalle.legajo.estado);
  // VEN-491: el aviso de carencia vive dentro de este panel, así que se muestra
  // aunque el titular ya haya elegido MS2. Esa marca es del MS2 ANTERIOR y la
  // tienen todos los que la regla bloquea (lo eligieron y lo pagaron en su
  // momento), así que el aviso no le llegaba a nadie.
  const ms2Reciente = detalle.infoComercial.ms2Reciente;
  const aplica =
    puede && ofertable && (detalle.legajo.planSuperadorElegido !== true || Boolean(ms2Reciente));

  const mutation = useMutation({
    mutationFn: () => reportarInteresadoSkytel(detalle.legajo.id),
    onSuccess: (data) => {
      setResultado(data.resultado);
      setConfirmando(false);
      refrescarBusqueda(queryClient, detalle.legajo.id);
    },
  });
  // Enviar la oferta MS2 por mail (mismo endpoint que el TMK; el backend deriva
  // el origen 'medicus' por el rol). Otro origen de la oferta a tener en cuenta.
  const ofertaMut = useMutation({
    mutationFn: () => enviarOfertaTmk(detalle.legajo.id),
    onSuccess: (data) => {
      setOfertaEnviada({ email: data.emailDestinatario });
      setConfirmarOferta(false);
      refrescarBusqueda(queryClient, detalle.legajo.id);
    },
  });
  // Copiar el link de la oferta SIN mandar mail: lo genera/reusa y lo deja en el
  // portapapeles para pasarlo por el canal que el operador prefiera (WhatsApp,
  // chat). Mismo endpoint que usa el TMK; el backend reusa el link vigente.
  const linkMut = useMutation({
    mutationFn: () => copiarLinkOferta(detalle.legajo.id),
    onSuccess: (data) => {
      setLinkCopiado(data.link);
      void navigator.clipboard?.writeText(data.link);
      refrescarBusqueda(queryClient, detalle.legajo.id);
    },
  });

  if (!aplica) return null;

  // VEN-491: estuvo en MS2 dentro de los últimos 12 meses. Se muestra el motivo
  // en vez de los botones: el server igual rechazaría la oferta, y "Reportar
  // como interesado" mandaría a SkyTel a llamar a alguien que no puede pasar.
  if (ms2Reciente) {
    return (
      <section
        style={{
          padding: '10px 14px',
          background: '#fffbeb',
          border: '1px solid rgba(180,83,9,0.25)',
          borderRadius: 12,
        }}
      >
        <h4 className="heading-6" style={{ margin: 0, color: '#92400e', fontSize: 14 }}>
          Todavía no puede cambiar al Plan MS2
        </h4>
        <p style={{ fontSize: 12, color: '#78350f', margin: '2px 0 0' }}>{ms2Reciente}</p>
      </section>
    );
  }

  const titularNombre = `${detalle.titular.nombres} ${detalle.titular.apellSolt}`.trim();
  const errorMsg = mutation.isError ? msgErrorApi(mutation.error, 'No se pudo reportar. Reintentá.') : null;

  return (
    <section
      style={{
        padding: '10px 14px',
        background: '#f0f7ff',
        border: '1px solid rgba(37,99,235,0.18)',
        borderRadius: 12,
      }}
    >
      {/* Una sola franja: la ficha es de la persona, la acción comercial no la tapa. */}
      <div className="rowBetween" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <h4 className="heading-6" style={{ margin: 0, color: '#1d4ed8', fontSize: 14 }}>
            ¿Quiere cambiar al Plan MS2?
          </h4>
          <p style={{ fontSize: 12, color: '#475569', margin: '2px 0 0' }}>
            Reportalo como interesado (SkyTel lo llama), mandale la oferta por mail o copiá el link.
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            className="btn btn-primary btn--sm"
            onClick={() => {
              setResultado(null);
              setConfirmando(true);
            }}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <PhoneCall size={14} />
            <span>Reportar como interesado</span>
          </button>
          <button
            type="button"
            className="btn btn-ghost btn--sm"
            disabled={!detalle.titular.email}
            title={detalle.titular.email ? undefined : 'El titular no tiene email cargado'}
            onClick={() => {
              setOfertaEnviada(null);
              setConfirmarOferta(true);
            }}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <Mail size={14} />
            <span>Enviar oferta por mail</span>
          </button>
          <button
            type="button"
            className="btn btn-ghost btn--sm"
            disabled={linkMut.isPending}
            onClick={() => {
              setLinkCopiado(null);
              setAvisoLink(true);
            }}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <Copy size={14} />
            <span>{linkMut.isPending ? 'Generando…' : 'Copiar link'}</span>
          </button>
        </div>
      </div>

      {resultado === 'generado' && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            background: 'rgba(21,128,61,0.1)',
            border: '1px solid rgba(21,128,61,0.3)',
            borderRadius: 8,
            fontSize: 13,
            color: '#15803d',
          }}
        >
          ✓ Listo. <strong>{titularNombre}</strong> fue reportado como interesado y enviado a SkyTel.
          Se van a poner en contacto por teléfono.
        </div>
      )}
      {resultado === 'ya_estaba' && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            background: 'rgba(37,99,235,0.08)',
            border: '1px solid rgba(37,99,235,0.25)',
            borderRadius: 8,
            fontSize: 13,
            color: '#1d4ed8',
          }}
        >
          Este contacto ya está en SkyTel. Se van a poner en contacto con él vía llamada.
        </div>
      )}

      {ofertaEnviada && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            background: 'rgba(21,128,61,0.1)',
            border: '1px solid rgba(21,128,61,0.3)',
            borderRadius: 8,
            fontSize: 13,
            color: '#15803d',
          }}
        >
          ✓ Mail con la oferta MS2 enviado a <strong>{ofertaEnviada.email}</strong>. El link vence en 7
          días.
        </div>
      )}

      {linkCopiado && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            background: 'rgba(21,128,61,0.1)',
            border: '1px solid rgba(21,128,61,0.3)',
            borderRadius: 8,
            fontSize: 13,
            color: '#15803d',
            wordBreak: 'break-all',
          }}
        >
          ✓ Link copiado al portapapeles. Pegalo en el canal que prefieras (vence en 7 días):{' '}
          <strong>{linkCopiado}</strong>
        </div>
      )}
      {linkMut.isError && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            background: 'rgba(220,38,38,0.08)',
            border: '1px solid rgba(220,38,38,0.25)',
            borderRadius: 8,
            fontSize: 13,
            color: '#b91c1c',
          }}
        >
          {msgErrorApi(linkMut.error, 'No pudimos generar el link. Reintentá.')}
        </div>
      )}

      {confirmando && (
        <ConfirmarReporteSkytelModal
          titular={titularNombre}
          legajoNumero={detalle.legajo.numero}
          enviando={mutation.isPending}
          error={errorMsg}
          onCancelar={() => setConfirmando(false)}
          onConfirmar={() => mutation.mutate()}
        />
      )}
      {avisoLink && (
        <AvisoAtribucionModal
          accion="link"
          atribuidoA={detalle.infoComercial.ofertaTmkVigente?.generadoPor}
          yoEmail={user?.email}
          onCancelar={() => setAvisoLink(false)}
          onContinuar={() => {
            setAvisoLink(false);
            linkMut.mutate();
          }}
        />
      )}
      {confirmarOferta && (
        <ConfirmarEnvioModal
          email={detalle.titular.email ?? '(sin email)'}
          monto={detalle.infoComercial.montoMs2}
          legajoNumero={detalle.legajo.numero}
          atribuidoA={detalle.infoComercial.ofertaTmkVigente?.generadoPor}
          yoEmail={user?.email}
          enviando={ofertaMut.isPending}
          error={ofertaMut.isError ? msgErrorApi(ofertaMut.error, 'No pudimos enviar el mail. Reintentá.') : null}
          onCancelar={() => setConfirmarOferta(false)}
          onConfirmar={() => ofertaMut.mutate()}
        />
      )}
    </section>
  );
}

function ConfirmarReporteSkytelModal(props: {
  titular: string;
  legajoNumero: string;
  enviando: boolean;
  error: string | null;
  onCancelar: () => void;
  onConfirmar: () => void;
}) {
  return (
    <ModalOverlay onClose={props.onCancelar}>
      <div
        className="card"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(460px, 100%)', display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <h3 className="heading-5" style={{ margin: 0 }}>
          Reportar como interesado
        </h3>
        <p className="text-element-mid" style={{ margin: 0, lineHeight: 1.55 }}>
          Vas a reportar a <strong>{props.titular}</strong> (legajo <strong>{props.legajoNumero}</strong>)
          como interesado en el cambio al <strong>Plan MS2</strong>. Lo enviamos a SkyTel para que lo
          contacten por teléfono.
        </p>
        {props.error && (
          <p className="status status-error" style={{ display: 'block', margin: 0 }}>
            {props.error}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={props.onCancelar}
            disabled={props.enviando}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={props.onConfirmar}
            disabled={props.enviando}
          >
            {props.enviando ? 'Enviando…' : 'Sí, reportar'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function ConfirmarEnvioModal(props: {
  email: string;
  monto: number;
  legajoNumero: string;
  /** A quién está atribuida hoy la oferta vigente (si hay). */
  atribuidoA?: AgenteAtribuido | null;
  yoEmail?: string | null;
  onCancelar: () => void;
  onConfirmar: () => void;
  enviando: boolean;
  error: string | null;
}) {
  return (
    <ModalOverlay onClose={props.onCancelar}>
      <div
        className="card"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(440px, 100%)', display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <h3 className="heading-5" style={{ margin: 0 }}>
          ¿Enviar oferta MS2?
        </h3>
        <p className="text-element-mid" style={{ margin: 0, lineHeight: 1.55 }}>
          Vas a enviar un mail a <strong>{props.email}</strong> con la oferta del Plan Medicus 2
          por <strong>{fmtMoney(props.monto)} / mes</strong> para el legajo{' '}
          <strong>{props.legajoNumero}</strong>. El link vence en 7 días.
        </p>
        <AvisoAtribucionTexto accion="mail" atribuidoA={props.atribuidoA} yoEmail={props.yoEmail} />
        {props.error && (
          <p className="status status-error" style={{ display: 'block', margin: 0 }}>
            {props.error}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={props.onCancelar}
            disabled={props.enviando}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={props.onConfirmar}
            disabled={props.enviando}
          >
            {props.enviando ? 'Enviando…' : 'Sí, enviar mail'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
